import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { view, utils, i18next } from '@yola/ws-sdk';
import highlighter from 'src/js/modules/highlighter';
import useHandlePosition from 'src/js/modules/extensions/common/hooks/use-handle-position';
import calculateOffsetTop from 'src/js/modules/extensions/common/helpers/calculate-offset-top';
import useDrag from 'src/js/modules/extensions/common/hooks/use-drag';
import ResizeHandle from 'src/js/modules/extensions/common/components/resize-handle';
import resizeToolPosition from 'src/js/modules/extensions/common/constants/resize-handle-positions';
import useEventListener from 'src/js/modules/utils/custom-hooks/use-event-listener';
import ResizeTooltip from 'src/js/modules/extensions/registry/ws-image/components/resize-tooltip';
import useMatchBreakpoint from 'src/js/modules/utils/custom-hooks/use-match-breakpoint';
import getDefaultTraits from 'src/js/modules/analytics/segment/helpers/get-default-traits';
import highlighterOffset from 'src/js/modules/highlighter/helpers/highlighter-offset';
import segment from 'src/js/modules/analytics/segment';
import getColumnsAlignment from '../helpers/get-column-alignment';
import {
  MIN_COLUMN_WIDTH,
  COLUMN_WIDTH_ATTRIBUTE,
  FLEX_BASIS_PROPERTY,
  MAX_WIDTH_PROPERTY,
} from '../constants';

const {
  track,
  constants: { events },
} = segment;

const getCaptions = () => ({
  shiftPressed: i18next.t('Release <b>Shift</b> to resize this column only'),
  shiftReleased: i18next.t('Hold <b>Shift</b> to resize all columns '),
  default: i18next.t('Drag to resize'),
});

const DESKTOP_BREAKPOINT = '992px';

const ColumnResizeTool = ({
  elementId,
  appearanceStyle,
  onActionStart,
  onActionEnd,
  onMouseEnter = utils.noop,
  onMouseLeave = utils.noop,
}) => {
  const element = useMemo(() => view.accessors.getLiveElement(elementId), [elementId]);
  const columnPadding = useMemo(() => {
    const liveDoc = view.accessors.getLiveDocument();
    const computedStyle = liveDoc.defaultView.getComputedStyle(element);
    return parseInt(computedStyle.paddingLeft, 10);
  }, [element]);
  const columnContainer = useMemo(() => element.parentElement, [element]);
  const columnContainerAlignment = useMemo(
    () => getColumnsAlignment(columnContainer),
    [columnContainer]
  );
  const isFillEnabled = useMemo(
    () => columnContainer.getAttribute('width') === 'fill',
    [columnContainer]
  );
  const allColumns = useMemo(() => Array.from(columnContainer.children), [columnContainer]);
  const multiElementResizeAvailable = useMemo(() => allColumns.length > 1, [allColumns]);
  const captions = useMemo(getCaptions, []);

  const [shiftPressed, setShiftStatus] = useState(false);
  const [tooltipPosition, showTooltip] = useState(null);
  const [isResizing, setResizingStatus] = useState(false);
  const leftHandleRef = useRef(null);
  const rightHandleRef = useRef(null);
  const currentRect = useRef(null);
  const containerRect = useRef(null);
  const activeDragHandlerPos = useRef(null);
  const indent = highlighterOffset.get();
  const isDesktopView = useMatchBreakpoint(DESKTOP_BREAKPOINT);

  const positions = useMemo(() => {
    switch (columnContainerAlignment) {
      case 'center':
        return {
          [resizeToolPosition.CENTER_LEFT]: {
            correctionX: columnPadding - indent,
          },
          [resizeToolPosition.CENTER_RIGHT]: {
            correctionX: -columnPadding + indent,
          },
        };
      case 'right':
        return {
          [resizeToolPosition.CENTER_LEFT]: {
            correctionX: columnPadding - indent,
          },
        };

      default:
        return {
          [resizeToolPosition.CENTER_RIGHT]: {
            correctionX: -columnPadding + indent,
          },
        };
    }
  }, [columnPadding, columnContainerAlignment, indent]);

  const [handlePosition, updateHandlePosition] = useHandlePosition({
    element,
    positions,
  });

  const showHighlighter = useCallback(
    (options = {}) => {
      const { withElementSize = false } = options;
      highlighter.operations.show([element], {
        forceUpdate: true,
        labelGap: 'medium',
        withElementSize,
      });
    },
    [element]
  );

  useEffect(() => {
    if (isDesktopView) {
      highlighter.operations.hide();
      showHighlighter();
    }

    return () => {
      highlighter.operations.hide();
    };
  }, [showHighlighter, isDesktopView]);

  const calculateNewWidth = useCallback(
    (distance) => {
      const { width: containerWidth } = containerRect.current;
      const { width: currentWidth } = currentRect.current;
      let distanceWidth;

      switch (columnContainerAlignment) {
        case 'center':
          if (activeDragHandlerPos.current === 'left') {
            distanceWidth = -distance[0] * 2;
          } else {
            distanceWidth = distance[0] * 2;
          }
          break;
        case 'right':
          distanceWidth = -distance[0];
          break;
        default:
          [distanceWidth] = distance;
      }

      const newAbsoluteWidth = currentWidth + distanceWidth;
      const width = Math.max(MIN_COLUMN_WIDTH, Math.min(newAbsoluteWidth, containerWidth));
      return ((width / containerWidth) * 100).toFixed(4);
    },
    [columnContainerAlignment]
  );

  const handleDrag = (distance) => {
    const width = calculateNewWidth(distance);

    if (shiftPressed) {
      allColumns.forEach((column) => {
        column.style.setProperty(FLEX_BASIS_PROPERTY, `${width}%`);
        column.style.setProperty(MAX_WIDTH_PROPERTY, `${width}%`);
      });
    } else {
      element.style.setProperty(FLEX_BASIS_PROPERTY, `${width}%`);
      element.style.setProperty(MAX_WIDTH_PROPERTY, `${width}%`);
    }

    updateHandlePosition();
  };

  const handleDragEnd = (distance) => {
    const width = calculateNewWidth(distance);

    let operations = [];

    if (shiftPressed) {
      const newOperations = Array.from(allColumns).map((column) => {
        const elId = view.accessors.getLiveElementId(column);
        return [
          view.operations.setResponsiveAttribute,
          [elId, COLUMN_WIDTH_ATTRIBUTE, `${width}%`, 'lg'],
        ];
      });

      operations = operations.concat(newOperations);
    } else {
      operations.push([
        view.operations.setResponsiveAttribute,
        [elementId, COLUMN_WIDTH_ATTRIBUTE, `${width}%`, 'lg'],
      ]);
    }

    if (isFillEnabled) {
      const columnElementId = view.accessors.getLiveElementId(columnContainer);
      operations.push([view.operations.removeElementAttribute, [columnElementId, 'width']]);
    }

    view.operations.bulkViewOperations(operations);

    allColumns.forEach((column) => {
      column.style.removeProperty(MAX_WIDTH_PROPERTY);
      column.style.removeProperty(FLEX_BASIS_PROPERTY);
    });

    const oldWidth = Math.round(currentRect.current.width);
    const newWidth = Math.round(Math.round((width * containerRect.current.width) / 100));

    return {
      newWidth: `${newWidth}px`,
      oldWidth: `${oldWidth}px`,
    };
  };

  const resetColumnsWidth = () => {
    allColumns.forEach((column) => {
      if (column === element) return;
      column.style.removeProperty(FLEX_BASIS_PROPERTY);
      column.style.removeProperty(MAX_WIDTH_PROPERTY);
    });
  };

  const handleMouseEnter = (e) => {
    const { target } = e;
    onMouseEnter();

    let position = resizeToolPosition.CENTER_RIGHT;
    if (target === leftHandleRef.current) {
      position = resizeToolPosition.CENTER_LEFT;
    }

    if (!isResizing) {
      showTooltip(position);
    }
  };

  const handleMouseLeave = () => {
    onMouseLeave();
    if (!isResizing) {
      showTooltip(null);
    }
  };

  useDrag(
    [leftHandleRef, rightHandleRef],
    {
      onStart(event) {
        activeDragHandlerPos.current = event.target === leftHandleRef.current ? 'left' : 'right';
        showHighlighter({ isGroup: shiftPressed, withElementSize: true });
        containerRect.current = element.parentElement.getBoundingClientRect();
        currentRect.current = element.getBoundingClientRect();
        setResizingStatus(true);
        showTooltip(null);
        if (isFillEnabled) {
          columnContainer.removeAttribute('width');
        }

        onActionStart();

        track(events.COLUMN_RESIZING_INITIATED, {
          ...getDefaultTraits(elementId),
        });
      },

      onMove(distance) {
        handleDrag(distance);
        showHighlighter({ withElementSize: true });
      },

      onEnd(distance) {
        setResizingStatus(false);
        const { newWidth, oldWidth } = handleDragEnd(distance, true);

        onActionEnd();
        if (shiftPressed) {
          const otherColumns = allColumns.filter((column) => column !== element);
          highlighter.operations.hide(otherColumns);
        }
        track(events.COLUMN_RESIZING_COMPLETED, {
          ...getDefaultTraits(elementId),
          affectedColumns: shiftPressed ? allColumns.length : 1,
          newWidth,
          oldWidth,
          shiftKeyPressed: shiftPressed,
        });
      },
    },
    { throttleMove: true }
  );

  const handleKey = ({ shiftKey }) => {
    if (isResizing && shiftKey !== shiftPressed) {
      if (shiftKey) {
        const flexBasis = element.style.getPropertyValue(FLEX_BASIS_PROPERTY);
        allColumns.forEach((column) => {
          column.style.setProperty(FLEX_BASIS_PROPERTY, flexBasis);
          column.style.setProperty(MAX_WIDTH_PROPERTY, flexBasis);
        });

        highlighter.operations.show(allColumns, {
          isGroup: true,
          withElementSize: true,
        });
      } else {
        resetColumnsWidth();
        const otherColumns = allColumns.filter((column) => column !== element);
        highlighter.operations.hide(otherColumns);
      }
      updateHandlePosition();
    }

    setShiftStatus(shiftKey);
  };

  const keyEventCallback = multiElementResizeAvailable ? handleKey : utils.noop;

  useEventListener('keydown', keyEventCallback, document);
  useEventListener('keyup', keyEventCallback, document);
  useEventListener('keydown', keyEventCallback, element.ownerDocument);
  useEventListener('keyup', keyEventCallback, element.ownerDocument);

  if (
    !isDesktopView ||
    (!handlePosition[resizeToolPosition.CENTER_LEFT] &&
      !handlePosition[resizeToolPosition.CENTER_RIGHT])
  ) {
    return null;
  }

  const leftPosition = handlePosition[resizeToolPosition.CENTER_LEFT];
  const rightPosition = handlePosition[resizeToolPosition.CENTER_RIGHT];
  const pos = leftPosition || rightPosition;

  const offtop = calculateOffsetTop(element, pos[1]);

  return (
    <React.Fragment>
      {columnContainerAlignment !== 'left' && (
        <ResizeHandle
          ref={leftHandleRef}
          left={leftPosition[0]}
          top={offtop}
          shape="oval"
          direction="left-right"
          appearanceStyle={appearanceStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      )}
      {columnContainerAlignment !== 'right' && (
        <ResizeHandle
          ref={rightHandleRef}
          left={rightPosition[0]}
          top={offtop}
          shape="oval"
          direction="left-right"
          appearanceStyle={appearanceStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      )}

      {tooltipPosition && (
        <ResizeTooltip
          left={handlePosition[tooltipPosition][0]}
          top={handlePosition[tooltipPosition][1]}
          direction="down"
        >
          {multiElementResizeAvailable && (
            <div
              // eslint-disable-next-line yola/react/no-danger
              dangerouslySetInnerHTML={{
                __html: shiftPressed ? captions.shiftPressed : captions.shiftReleased,
              }}
            />
          )}
          {!multiElementResizeAvailable && captions.default}
        </ResizeTooltip>
      )}
    </React.Fragment>
  );
};

ColumnResizeTool.propTypes = {
  elementId: PropTypes.string.isRequired,
  appearanceStyle: PropTypes.shape().isRequired,
  onActionStart: PropTypes.func.isRequired,
  onActionEnd: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
};

export default ColumnResizeTool;
